import { useEffect, useRef } from 'react';

/**
 * The rules summary, shown from the question mark in the masthead.
 *
 * The text is the project owner's, transcribed as given. It is a
 * reference for players mid-game, not documentation, so it stays terse
 * and is not rephrased.
 */

interface HelpProps {
  onClose: () => void;
  /**
   * Whether debris is in play, when the reader is in a game.
   *
   * `null` outside a game -- the lobby, the course editor -- where there
   * is no game to answer for. The debris note is shown then, since it is
   * a base rule and the summary is being read in general rather than
   * against a particular game's settings.
   */
  debris?: boolean | null;
  /**
   * Whether the game is in turn mode, which nobody is asked anything in
   * once programming closes: the chop shop, the double wrench, the power
   * down and a destroyed robot's return settle themselves. `null` outside
   * a game, read as normal mode.
   */
  turnMode?: boolean | null;
}

export function Help({ onClose, debris = null, turnMode = null }: HelpProps) {
  const panel = useRef<HTMLDivElement>(null);

  // Escape closes it, and focus moves into the panel so a keyboard user
  // is not left behind on the button that opened it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    panel.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="help-backdrop"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="help-panel plate"
        role="dialog"
        aria-modal="true"
        aria-label="Rules summary"
        tabIndex={-1}
        ref={panel}
        // A click inside must not reach the backdrop, which closes.
        onClick={(e) => e.stopPropagation()}
      >
        <div className="hazard hazard-rule" role="presentation" />

        <div className="help-body">
          <div className="spread">
            <h3>Rules summary</h3>
            <button className="secondary" onClick={onClose}>Close</button>
          </div>

          <ul className="help-notes">
            <li>Robots are virtual on Turn 1, and by register afterwards.</li>
            <li>Millenium Falcom applies to timed pits and flame throwers.</li>
            <li>Must be on last flag on last card on last turn to win.</li>
            <li>Robots on a Pit Stop deactivate the next phase.</li>
            {/* Hidden when the host switched debris off, so a player is
                never told a rule this game does not use. */}
            {debris !== false && (
              <li>
                Debris appears when a robot is destroyed or receives 2+ damage
                during a register phase. A robot that enters and exits debris
                during a single register phase receives 1 damage.
              </li>
            )}
          </ul>

          <p className="eyebrow">Turn sequence</p>

          <ol className="help-sequence">
            <li>
              {turnMode
                ? 'New robots keep the facing they had when they were destroyed'
                : 'New robots set direction (except Turn 1)'}
            </li>

            <li>
              Programming:
              <ul>
                <li>Robots set direction (Turn 1)</li>
                <li>Arrange program cards</li>
                {turnMode && <li>Switch on Options for the turn</li>}
                <li>Select power down</li>
              </ul>
            </li>

            <li>
              Board Elements:
              <ul>
                <li>Robots Move (Flamers damage)</li>
                <li>Express convey 1</li>
                <li>Express/Single convey 1</li>
                <li>Current convey 1</li>
                <li>Pushers push (by register phase)</li>
                <li>Gears rotate</li>
              </ul>
            </li>

            <li>
              Weapons Fire:
              <ul>
                <li>Lasers/Flamers/Crushers (by register phase) activate</li>
              </ul>
            </li>

            <li>
              End of Phase:
              <ul>
                <li>Touch checkpoints</li>
                <li>
                  {turnMode
                    ? 'Chop shop activates (reload the emptiest; new option on register 5)'
                    : 'Chop shop activates (reload/exchange)'}
                </li>
                <li>Robots devirtualize (except Turn 1)</li>
                <li>Pit Stop heals</li>
              </ul>
            </li>

            <li>
              End of Turn:
              <ul>
                <li>
                  {turnMode
                    ? 'Repair (double wrench: heal 2 at 2+ damage, otherwise an option)'
                    : 'Repair'}
                </li>
                <li>New options (as appropriate)</li>
                {turnMode && <li>Destroyed robots lose an option at random</li>}
                <li>Robots devirtualize (Turn 1)</li>
                <li>Win game</li>
                {!turnMode && <li>Decide to stay powered down (if powered down)</li>}
              </ul>
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
}
